import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const RECHT_ICONS = {
  'objecten:lezen': '🏗️',
  'onderhoud:lezen': '🛠️',
  'werkorders:lezen': '✅',
  'logboek:lezen': '📝',
  'storingen:lezen': '🚨',
  'storingen:melden': '📣',
  'datum:verzoeken': '📅',
  'documenten:lezen': '📄',
  'accounts:beheren': '👥',
};

const C = {
  accent: '#4f46e5', text: '#1f2937', textDim: '#6b7280', textMute: '#9ca3af',
  border: '#e5e7eb', rowBorder: '#f3f4f6', card: '#ffffff', headBg: '#f9fafb',
};

function WerkbonSecties() {
  const [rijen, setRijen] = React.useState([]);
  const [busy, setBusy] = React.useState(null);
  const [msg, setMsg] = React.useState('');
  const load = () => api.get('/opdrachtgevers').then(r => setRijen(r.data.opdrachtgevers || r.data.klanten || r.data || [])).catch(() => {});
  React.useEffect(() => { load(); }, []);
  const SECTIES = [['uren','Uren'],['lmra','LMRA'],['checklist','Checklist'],['opmerkingen','Opmerkingen'],['logboek','Logboek'],['fotos',"Foto's"]];
  const toggle = async (rij, key) => {
    const nieuw = { uren: true, lmra: true, checklist: true, opmerkingen: true, logboek: true, fotos: true, ...(rij.pdf_secties || {}) };
    nieuw[key] = !(nieuw[key] !== false);
    setBusy(rij.id + key);
    try {
      await api.put('/opdrachtgevers/' + rij.id, { pdf_secties: nieuw });
      setRijen(rs => rs.map(r => r.id === rij.id ? { ...r, pdf_secties: nieuw } : r));
      setMsg('Opgeslagen'); setTimeout(() => setMsg(''), 1500);
    } catch (e) { setMsg('Opslaan mislukt'); }
    setBusy(null);
  };
  if (!rijen.length) return null;
  return (
    <div style={{ marginTop: 32 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>📄 Werkbon-secties per opdrachtgever</h2>
      <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 14px' }}>Bepaalt welke secties de opdrachtgever ziet bij het downloaden van een werkbon in het portaal. Wijzigingen worden direct opgeslagen.</p>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ display: 'flex', padding: '10px 14px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 12.5, fontWeight: 700, color: '#64748b' }}>
          <div style={{ flex: 2 }}>Opdrachtgever</div>
          {SECTIES.map(([k, l]) => <div key={k} style={{ flex: 1, textAlign: 'center' }}>{l}</div>)}
        </div>
        {rijen.map(rij => (
          <div key={rij.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ flex: 2, fontSize: 14, fontWeight: 600 }}>{rij.naam}</div>
            {SECTIES.map(([k]) => (
              <div key={k} style={{ flex: 1, textAlign: 'center' }}>
                <input type="checkbox" checked={(rij.pdf_secties?.[k]) !== false} disabled={busy === rij.id + k}
                  onChange={() => toggle(rij, k)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
              </div>
            ))}
          </div>
        ))}
      </div>
      {msg && <div style={{ marginTop: 8, fontSize: 13, color: msg === 'Opgeslagen' ? '#059669' : '#b91c1c', fontWeight: 600 }}>{msg}</div>}
    </div>
  );
}

const PortalBeheer = () => {
  const { gebruiker, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [rollen, setRollen] = useState([]);
  const [rechten, setRechten] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  const magBeheren = gebruiker && hasPermission && hasPermission('portal:beheer');

  const laden = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [rRollen, rRechten] = await Promise.all([
        api.get('/portal-beheer/rollen'),
        api.get('/portal-beheer/rechten'),
      ]);
      const rollenData = rRollen.data.rollen || [];
      setRollen(rollenData);
      setRechten(rRechten.data.rechten || []);
      const m = {};
      rollenData.forEach((rol) => { m[rol.id] = new Set(rol.rechten || []); });
      setMatrix(m);
      setDirty(false);
    } catch (err) {
      console.error(err);
      setError('Kon portal-rollen niet laden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (gebruiker?.id && !magBeheren) { navigate('/'); return; }
    if (magBeheren) laden();
  }, [gebruiker, magBeheren, laden, navigate]);

  const toggle = (rolId, code) => {
    setMatrix((prev) => {
      const next = { ...prev };
      const set = new Set(next[rolId]);
      if (set.has(code)) set.delete(code); else set.add(code);
      next[rolId] = set;
      return next;
    });
    setDirty(true);
    setSavedMsg('');
  };

  const opslaan = async () => {
    setSaving(true); setError(null);
    try {
      for (const rol of rollen) {
        const codes = Array.from(matrix[rol.id] || []);
        await api.put(`/portal-beheer/rollen/${rol.id}/rechten`, { rechten: codes });
      }
      setDirty(false);
      setSavedMsg('Opgeslagen ✓');
      setTimeout(() => setSavedMsg(''), 3000);
    } catch (err) {
      console.error(err);
      setError('Opslaan mislukt. Probeer opnieuw.');
    } finally {
      setSaving(false);
    }
  };

  if (!magBeheren) return null;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ maxWidth: 920, margin: '0 auto' }}>
        <h1 style={{ color: C.text, fontSize: 24, margin: '0 0 4px 0' }}>Klantportaal — rollen &amp; rechten</h1>
        <p style={{ color: C.textDim, fontSize: 14, margin: '0 0 24px 0', maxWidth: 700, lineHeight: 1.5 }}>
          Bepaal per rol welke onderdelen klanten in het portaal mogen zien of doen.
        </p>

        {error && (
          <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '12px 16px', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }}>{error}</div>
        )}

        {loading ? (
          <p style={{ color: C.textDim }}>Laden…</p>
        ) : (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: C.headBg, borderBottom: `1px solid ${C.border}` }}>
                  <th style={{ textAlign: 'left', padding: '12px 16px', color: C.textDim, fontWeight: 600, fontSize: 13 }}>Onderdeel</th>
                  {rollen.map((rol) => (
                    <th key={rol.id} style={{ padding: '12px 16px', color: C.accent, fontWeight: 600, textAlign: 'center', fontSize: 14 }}>{rol.naam}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rechten.map((recht) => (
                  <tr key={recht.code} style={{ borderBottom: `1px solid ${C.rowBorder}` }}>
                    <td style={{ padding: '12px 16px', color: C.text, fontSize: 14 }}>
                      <span style={{ marginRight: 8 }}>{RECHT_ICONS[recht.code] || '•'}</span>
                      {recht.omschrijving || recht.code}
                    </td>
                    {rollen.map((rol) => {
                      const aan = (matrix[rol.id] || new Set()).has(recht.code);
                      return (
                        <td key={rol.id} style={{ textAlign: 'center', padding: '12px 16px' }}>
                          <input
                            type="checkbox"
                            checked={aan}
                            onChange={() => toggle(rol.id, recht.code)}
                            style={{ width: 17, height: 17, accentColor: C.accent, cursor: 'pointer' }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 20 }}>
            <button
              onClick={opslaan}
              disabled={!dirty || saving}
              style={{
                background: dirty && !saving ? C.accent : '#e5e7eb',
                color: dirty && !saving ? '#ffffff' : C.textMute,
                border: 'none', padding: '9px 18px', borderRadius: 6, fontSize: 14, fontWeight: 600,
                cursor: dirty && !saving ? 'pointer' : 'default',
              }}
            >
              {saving ? 'Opslaan…' : 'Wijzigingen opslaan'}
            </button>
            {savedMsg && <span style={{ color: '#059669', fontSize: 14, fontWeight: 600 }}>{savedMsg}</span>}
            {dirty && !saving && <span style={{ color: C.textDim, fontSize: 13 }}>Niet-opgeslagen wijzigingen</span>}
          </div>
        )}
      </div>
      <WerkbonSecties />
    </div>
  );
};

export default PortalBeheer;
